import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SettingsPanel } from "@/AdminCatalog";
import { ProductsTable } from "@/AdminProducts";
import { EnquiriesTable } from "@/AdminEnquiries";
import { SupportTable } from "@/AdminSupport";
import { OrderTrackingPanel } from "@/AdminTracking";
import { Fragment } from "react";
import { API, http, fail } from "@/adminApi";
import "@/AdminPage.css";

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
const STATUSES = ["new", "processing", "shipped", "delivered", "cancelled"];

const startLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthCallback() {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    http.post("/auth/session", { session_id: sessionId })
      .then((r) => navigate("/admin", { replace: true, state: { user: r.data } }))
      .catch((e) => navigate("/admin", { replace: true, state: { authError: e.response?.data?.detail || "Sign-in failed" } }));
  }, [navigate]);
  return null;
}

function LoginPanel({ error }) {
  return (
    <div className="admin-login">
      <UserRound />
      <div>
        <b>Google admin access</b>
        <span>Sign in with the authorized account to manage catalog and orders.</span>
        {error && <span className="form-error" data-testid="admin-auth-error">{error}</span>}
      </div>
      <button className="btn btn-outline" onClick={startLogin} data-testid="admin-google-login">Continue with Google</button>
    </div>
  );
}

function OrdersTable() {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const onSaved = (o) => setOrders((list) => list.map((x) => (x.order_id === o.order_id ? o : x)));
  useEffect(() => { http.get("/admin/orders").then((r) => setOrders(r.data)).catch(fail); }, []);
  const setStatus = async (id, fulfillment_status) => {
    try {
      const r = await http.put(`/admin/orders/${id}`, { fulfillment_status });
      setOrders((list) => list.map((o) => (o.order_id === id ? r.data : o)));
      toast.success(`Order ${id} marked ${fulfillment_status}`);
    } catch (e) { fail(e); }
  };
  return (
    <section className="admin-section" data-testid="admin-orders">
      <h2>Orders <i>{orders.length}</i></h2>
      {!orders.length ? <p className="admin-empty" data-testid="admin-orders-empty">No orders yet.</p> : (
        <div className="admin-table-wrap"><table className="admin-table">
          <thead><tr><th>Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Payment</th><th>Order status</th><th>Emails</th><th>Tracking</th></tr></thead>
          <tbody>{orders.map((o) => (
            <Fragment key={o.order_id}><tr data-testid={`admin-order-row-${o.order_id}`}>
              <td><b>{o.order_id}</b><span>{new Date(o.created_at).toLocaleString()}</span></td>
              <td><b>{o.customer?.name}</b><span>{o.customer?.email} · {o.customer?.phone}</span></td>
              <td><span>{o.lines?.map((l) => `${l.quantity}× ${l.name} ${l.variant || ""}`).join(", ")}</span></td>
              <td><b>{inr(o.total_inr)}</b><span>{usd(o.subtotal_usd)} @ ₹{o.exchange_rate}/$</span></td>
              <td><span className={`pay-pill ${o.payment_status}`} data-testid={`admin-order-payment-${o.order_id}`}>{o.payment_status}</span></td>
              <td><select value={o.fulfillment_status || "new"} onChange={(e) => setStatus(o.order_id, e.target.value)} data-testid={`admin-order-status-${o.order_id}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></td>
              <td><span data-testid={`admin-order-emails-${o.order_id}`}>{o.confirmation_email_sent ? "Confirmation ✓" : "—"}{o.shipped_email_sent ? " · Shipped ✓" : ""}</span></td>
              <td><button className={openId === o.order_id ? "icon-action active" : "icon-action"} onClick={() => setOpenId(openId === o.order_id ? null : o.order_id)} data-testid={`admin-tracking-toggle-${o.order_id}`}>{(o.dispatch_status || "not_dispatched").replace(/_/g, " ")}{o.expected_delivery ? ` · ETA ${o.expected_delivery}` : ""}</button></td>
            </tr>
            {openId === o.order_id && <OrderTrackingPanel order={o} onSaved={onSaved} />}</Fragment>
          ))}</tbody>
        </table></div>
      )}
    </section>
  );
}

export default function AdminPage({ location }) {
  const [me, setMe] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [payment, setPayment] = useState(null);
  useEffect(() => {
    if (!me) http.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}).finally(() => setChecking(false));
    axios.get(`${API}/payment/status`).then((r) => setPayment(r.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const logout = async () => { await http.post("/auth/logout"); setMe(null); };
  return (
    <main className="admin-page">
      <p className="eyebrow red">ADMIN / CONTROL ROOM</p>
      <h1>Store operations.</h1>
      {checking ? <p className="loading">Checking access…</p> : !me ? <LoginPanel error={location.state?.authError} /> : (
        <>
          <div className="admin-login">
            <UserRound />
            <div><b data-testid="admin-signed-in-email">Signed in as {me.email}</b><span>Payments: {payment?.configured ? `Cashfree connected (${payment.mode})` : "paused — Cashfree keys not added"}</span></div>
            <button className="btn btn-outline" onClick={logout} data-testid="admin-logout"><LogOut size={15} /> Sign out</button>
          </div>
          <SettingsPanel http={http} />
          <ProductsTable />
          <OrdersTable />
          <EnquiriesTable http={http} fail={fail} />
          <SupportTable />
        </>
      )}
    </main>
  );
}
